/**
 * Time Tracker field + admin dual-host narration scripts.
 */

export const TIME_TRACKER_NARRATIONS = {
  'time-tracker-help': [
    {
      title: 'Hours that payroll can trust',
      icon: '⏱️',
      turns: [
        { speaker: 'clara', speech: 'Welcome to Time Tracker. This is where field hours become payroll, actuals, and the numbers forecasting later compares to budget. If a code is wrong here, the rest of the portal inherits that mistake.' },
        { speaker: 'liam', speech: 'So the first question is not how many hours. It is which labour code those hours belong to. We will walk every surface you actually use: the log, daily, weekly, crews, analytics, and what to do when something is missing.' },
      ],
    },
    {
      title: 'Who uses what',
      icon: '👥',
      turns: [
        { speaker: 'liam', speech: 'Crew heads use Daily. One date, the whole crew. Weekly is for project managers, accounting, and a superadmin. Foremen, general foremen, and superintendents do not get Weekly. They enter the crew on Daily, including their own hours.' },
        { speaker: 'clara', speech: 'Admin and Crews buttons only appear if your role allows them. Cost clerks and the controller see Admin. Crew heads see Daily. If a button is missing, that is permission, not a broken page.' },
      ],
    },
    {
      title: 'The main hours log',
      icon: '📋',
      turns: [
        { speaker: 'clara', speech: 'The log is the read-only grid of every timesheet row you can see. Search, filter, and sort the columns. Draft rows are still being edited. Submitted rows are what payroll and the P D F reports use.' },
        { speaker: 'liam', speech: 'You can delete a row you own if the payroll period is still open. Active viewers in the toolbar show who else is on this page. The teal Help button is always here.' },
      ],
    },
    {
      title: 'Permissions you will hit',
      icon: '🔐',
      turns: [
        { speaker: 'liam', speech: 'Self scope means you only see your own hours. Team scope is your crew or assignments. All is the full job. Weekly employee pickers follow the same rule.' },
        { speaker: 'clara', speech: 'Admin writes return forbidden if you are not a cost clerk, controller, or superadmin. Period lock returns a locked status even for people who can normally edit.' },
      ],
    },
    {
      title: 'Where labour codes come from',
      icon: '📘',
      turns: [
        { speaker: 'clara', speech: 'The catalogue lives in Maestro and on Accounting, Coding Structure. Five-digit numbers. Twenty-one thousand is piping, twenty-two is M and R, and so on through twenty-nine for boilermaker. Extras are the same range plus sixty thousand.' },
        { speaker: 'liam', speech: 'Time Tracker does not pull that catalogue by itself. Accounting loads standard codes under Admin, Op Codes. Change Log extras now land in Admin, Extras automatically. Until a job-discipline is loaded, the dropdown is empty on purpose.' },
      ],
    },
    {
      title: 'Job plus discipline is the rule',
      icon: '🧭',
      turns: [
        { speaker: 'clara', speech: 'Every list in Time Tracker is a job plus a discipline. You always pick the project, then piping, M and R, electrical, and the rest. That is the rule, not a special case.' },
        { speaker: 'liam', speech: 'A job shows up when it has that discipline on the project, or when the job number already ends in those three letters, like three zero four three seven P I P. Standard codes, Change Log extras, and the timesheet dropdown all use that same pair. If you have not opened the pair, you have not opened the extra.' },
      ],
    },
    {
      title: 'Discipline ranges',
      icon: '🗺️',
      turns: [
        { speaker: 'liam', speech: 'Nine disciplines. Piping twenty-one, M and R twenty-two, electrical twenty-three, sheet metal twenty-four, pipe shop twenty-five, civil twenty-six, structural twenty-seven, tank twenty-eight, boilermaker twenty-nine.' },
        { speaker: 'clara', speech: 'Indirects like project manager, quality, safety, and room and board sit in the zero-one-thousand range. On the employee master, discipline and trade are the same field. One code, not two.' },
      ],
    },
    {
      title: 'Picking a code on the timesheet',
      icon: '🔎',
      turns: [
        { speaker: 'liam', speech: 'Choose the project-discipline first, like nine nine nine nine nine P F S. Then search the labour code by number or description. It locks as a chip so you cannot half-edit it. Extras show an amber badge.' },
        { speaker: 'clara', speech: 'Switch the project and the code clears on purpose, so piping hours never land on the pipe-shop list. If you cannot find a code, it was never loaded or someone turned it inactive.' },
      ],
    },
    {
      title: 'Daily crew timesheet',
      icon: '📅',
      turns: [
        { speaker: 'clara', speech: 'Daily is for crew heads only. Pick the date and the crew. Add a row per person, project, and labour code. Fill regular, time and a half, double, height, and shift. There is no draft. Saving the day writes the rows as submitted. Nobody else has to approve them.' },
        { speaker: 'liam', speech: 'If you are not a crew head, open Crews and name yourself the head. Foremen, general foremen, and superintendents enter time on Daily, including their own hours. One person can sit on more than one row if they charged more than one code that day.' },
      ],
    },
    {
      title: 'Daily allowances',
      icon: '💵',
      turns: [
        { speaker: 'liam', speech: 'Room and board, meals, and travel dollars are entered once per employee for the day, in Daily Allowances under the grid. Calculate previews the split across rows that already have a project, an operation code, and hours. The split follows regular hours plus time and a half plus double time. Height is left out. Calculate does not save.' },
        { speaker: 'clara', speech: 'One row with eight hours gets the whole amount. Two rows, six hours and two hours, split one hundred dollars of room and board into seventy five and twenty five. Cents that do not divide evenly land on the row with the most hours. The preview shows as a grey number in the empty cells, and a green calculated tag appears beside the name. A number typed in a row cell stays on that row. Submit writes the dollars. You can submit without clicking Calculate.' },
      ],
    },
    {
      title: 'Weekly personal timesheet',
      icon: '🗓️',
      turns: [
        { speaker: 'clara', speech: 'Weekly is one person for seven days. Canada weeks run Sunday through Saturday. United States weeks run Monday through Sunday. Use prev and next to move weeks.' },
        { speaker: 'liam', speech: 'Each row is one project plus one labour code across the week. Add rows if you charged more than one code. Hours auto-save as draft while you type. The week start follows that person’s payroll group: Canada starts Sunday, the United States starts Monday.' },
      ],
    },
    {
      title: 'Weekly hours grid',
      icon: '🧮',
      turns: [
        { speaker: 'liam', speech: 'Each day has regular, time and a half, double, and height. Shift is per day: day, afternoon, or night. Comments become day-off notes on the P D F reports.' },
        { speaker: 'clara', speech: 'A blank day is fine. A day with hours but no project or code will fail submit. Keep one honest row per code. Do not hide a second job inside a comment.' },
      ],
    },
    {
      title: 'Weekly allowances',
      icon: '🧾',
      turns: [
        { speaker: 'clara', speech: 'Weekly has a daily allowances strip for room and board, meals, and travel. Those totals split across that day\'s project rows by hours, the same idea as Daily.' },
        { speaker: 'liam', speech: 'The per-row breakdown is read-only so you can see the split. Change the daily totals, not the split, if a number looks wrong.' },
      ],
    },
    {
      title: 'Draft and submit',
      icon: '📝',
      turns: [
        { speaker: 'liam', speech: 'Weekly auto-saves drafts. Save Draft retries if the network blinked. Submit validates project, code, and hours. A day cannot go over twenty-four hours.' },
        { speaker: 'clara', speech: 'If the payroll period is locked you will get a locked error and nothing saves. Ask a cost clerk to unlock only if the week was closed by mistake.' },
      ],
    },
    {
      title: 'After you submit',
      icon: '✅',
      turns: [
        { speaker: 'clara', speech: 'Submitted rows show a submitted badge on the log. The P D F reports read submitted hours. Drafts stay off those reports. There is no approval queue after you submit.' },
        { speaker: 'liam', speech: 'You can change your own drafts while the week is open. After submit, a manager corrects the row. A locked week blocks field edits. Call Accounting if a posted number is wrong.' },
      ],
    },
    {
      title: 'What counts as hours',
      icon: '🧮',
      turns: [
        { speaker: 'clara', speech: 'Total hours are regular plus time and a half plus double. Overtime is typed. The sheet does not calculate it from a clock. One person cannot go over twenty four hours on one day.' },
        { speaker: 'liam', speech: 'Height, room and board, meals, travel dollars, and bus are allowances. They never add into total hours. Enter them once per person per day. They split across that day’s rows by hours, and you can pin a row to hold an exact amount.' },
      ],
    },
    {
      title: 'Who sees the hours',
      icon: '👁️',
      turns: [
        { speaker: 'liam', speech: 'Most people see their own hours, and the crew they head. Foremen and project managers also see their team. Accounting and a superadmin see every job.' },
        { speaker: 'clara', speech: 'Search, who is viewing, Help, Daily, Crews, and Analytics are on the toolbar for everyone. Weekly is for project managers, accounting, and a superadmin, on this page and on the phone. Admin and Audit stay with accounting and a superadmin. Payroll sees hours and the audit log, and still does not see Admin.' },
      ],
    },
    {
      title: 'A lock covers the payroll group',
      icon: '🔒',
      turns: [
        { speaker: 'clara', speech: 'Canada and the United States are locked separately. Locking Canada freezes that week for every Canadian employee, on every job. It is not a lock on one project.' },
        { speaker: 'liam', speech: 'The payroll group comes from the division name. A name that ends in U S is the United States. A name that ends in C A is Canada. If the division does not say the country, or the person has no division, the week lock does not cover them. Do not lock a week that people are still entering.' },
      ],
    },
    {
      title: 'The P D F and Maestro differ',
      icon: '📤',
      turns: [
        { speaker: 'liam', speech: 'The P D F uses submitted rows only. The Maestro payroll file includes draft and submitted hours that have not been removed. A line with zero hours is left out.' },
        { speaker: 'clara', speech: 'Removed rows stay out. The presentation job that starts with P R E S O dash T T dash zero zero one stays out. Submit drafts before export, or the file will contain hours the P D F does not show.' },
      ],
    },
    {
      title: 'Your hours stay',
      icon: '🛟',
      turns: [
        { speaker: 'clara', speech: 'Removing a timesheet hides the row. It does not destroy it. Daily and Weekly saves write an audit row with the hours, including room and board, meals, and travel. If that audit write fails, the hours are not kept. Accounting can bring a removed row back from the audit log. An employee is turned off, not wiped.' },
        { speaker: 'liam', speech: 'The hours and the audit log live in the same Azure database. Azure can restore that whole database to an earlier moment. A separate copy of the Time Tracker tables can be kept outside Azure, so the hours are not only in that one database. That copy is not a button on this page. Git keeps the program. It does not keep the hours.' },
      ],
    },
    {
      title: 'Crews from the field',
      icon: '👷',
      turns: [
        { speaker: 'liam', speech: 'Daily only works if you head a crew. Anyone can create that crew, name themselves the head, and add members. You can change your own crew and transfer it to someone else. Accounting and a superadmin can change any crew. Every change is written to the audit log.' },
        { speaker: 'clara', speech: 'On a daily row, the plus beside an employee copies that person onto a new row with no operation code. The plus beside an operation code copies that code onto a new row with no employee. If you have no crew yet, open Crews first and name yourself the head. Your own hours go on that daily row.' },
      ],
    },
    {
      title: 'Two ways hours reach payroll',
      icon: '🧭',
      turns: [
        { speaker: 'clara', speech: 'Jobs fall into two groups. If the job has a clerk, the foreman enters the crew hours, the clerk reviews them on the log, and then the payroll clerk, for example Faith, reviews them and enters them in Maestro.' },
        { speaker: 'liam', speech: 'If the job has no clerk, the foreman is done and the hours go straight to the payroll clerk. There is no clerk review in between. The portal does not add a second confirm button. Submitted rows are what those people review.' },
      ],
    },
    {
      title: 'Analytics is not payroll',
      icon: '📊',
      turns: [
        { speaker: 'clara', speech: 'Analytics charts hours by trade, op code, shift, and week. Use the date range to zoom. It is a picture of what was entered, not a payroll submit button.' },
        { speaker: 'liam', speech: 'If a bar looks empty, the filter or date range is hiding rows, or those hours are still draft. Open the log to confirm before you worry.' },
      ],
    },
    {
      title: 'When something is wrong',
      icon: '🛠️',
      turns: [
        { speaker: 'liam', speech: 'Empty dropdown means you have not opened that job-discipline pair, or it was never loaded. Inactive code means the gray status dot in Admin. Wrong project-discipline is why a code vanished after you switched jobs.' },
        { speaker: 'clara', speech: 'The employee picker is active people only. The Hiring and Layoff master has everyone, including terminated. If a name is missing, their status is not active, not that Entra hid them.' },
      ],
    },
    {
      title: 'Column legend',
      icon: '📖',
      turns: [
        { speaker: 'clara', speech: 'Regular is straight time. T twelve is time and a half. Double is double time. Height is height pay hours. Total hours is those three time buckets only.' },
        { speaker: 'liam', speech: 'Room and board, meals, travel dollars, and bus are allowances. Shifts are D for day, A for afternoon, N for night. Charge cleanly and payroll stays honest. Happy tracking.' },
      ],
    },
  ],

  'time-tracker-admin-help': [
    {
      title: 'Who Admin is for',
      icon: '🛡️',
      turns: [
        { speaker: 'clara', speech: 'Time Tracker Admin is for cost clerks, the controller, and superadmin. The portal checks that on the button and again on every write. Everyone else is refused.' },
        { speaker: 'liam', speech: 'Accounting, including payroll and accounts payable, can see everyone’s hours and the audit log. Only the cost clerk, the controller, and superadmin see these six cards. Field people use the other Help.' },
      ],
    },
    {
      title: 'The six cards',
      icon: '🗂️',
      turns: [
        { speaker: 'liam', speech: 'Op Codes is the standard labour list per job and discipline. Employees is the same master as Accounting Hiring and Layoff. Extras is change-order codes. Reports prints the four P D F packs. Period Lock freezes weeks. Maestro Export builds the nine-line payroll file.' },
        { speaker: 'clara', speech: 'Open Hiring and Layoff in Accounting when you need the full H R screen: ball in court, follow-up notes, audit. This Employees card is the same rows with a thinner editor.' },
      ],
    },
    {
      title: 'Job plus discipline is the rule',
      icon: '🧭',
      turns: [
        { speaker: 'liam', speech: 'Admin, Op Codes and Admin, Extras always open a job, then a discipline. Search the job number, pick the job, then pick piping or the matching trade. That is how every extra and every standard code is stored.' },
        { speaker: 'clara', speech: 'A job is listed when it has a discipline on the project, or when the job number already ends in P I P, M W R, E L E, and the rest. Change Log writes onto that same pair. If the extra is not on the screen, you have not opened that pair yet.' },
      ],
    },
    {
      title: 'Op Codes management',
      icon: '📋',
      turns: [
        { speaker: 'clara', speech: 'Pick the project-discipline, then add rows or import Excel. Codes must be numeric and at most five digits. They pad with zeros. The green dot means the timesheet can see it. Gray hides it from new entries without deleting history.' },
        { speaker: 'liam', speech: 'Export downloads what is on that job. Template gives clerks a blank sheet. If a dropdown is empty in the field, this list was never saved for that pair.' },
      ],
    },
    {
      title: 'Extras from Change Log',
      icon: '🔶',
      turns: [
        { speaker: 'liam', speech: 'When someone saves a Change Log extra number, the portal now writes the Time Tracker extra as well. You will see a Change Log badge. The code and description stay locked so the extra number does not drift.' },
        { speaker: 'clara', speech: 'Your job is the active toggle. Turn it off to hide it from timesheets. Keep plus Add Extra for odd legacy codes. Remove is a soft delete. Accounting restores from Audit. Extra ranges sit sixty thousand above the discipline labour range.' },
      ],
    },
    {
      title: 'Employees and the three counts',
      icon: '👥',
      turns: [
        { speaker: 'clara', speech: 'The footer count, about thirteen hundred, is Accounting\'s Hiring and Layoff master: everyone ever coded, including laid off. Entra is a different list: people with a Laframboise login email, about two hundred twenty-seven. Entra is not a subset of the master. Office people can have email and no four-digit code. Field craft can have a code and no email.' },
        { speaker: 'liam', speech: 'The yellow banner used to say two hundred because the review list was capped at two hundred rows. The real unmatched-active count is much larger, around eight hundred. Think of a Venn diagram: a small overlap in the middle, not thirteen hundred minus two hundred.' },
      ],
    },
    {
      title: 'Placeholder codes',
      icon: '🔢',
      turns: [
        { speaker: 'liam', speech: 'Official placeholders are one one one one Need, nine nine nine nine New, and nine nine nine three through nine nine nine eight for Need, discipline, role. Zero zero zero zero is forbidden.' },
        { speaker: 'clara', speech: 'Use them when Accounting is waiting on a real code. Clear them before payroll export so Maestro does not see a Need row as a person.' },
      ],
    },
    {
      title: 'Discipline equals trade',
      icon: '🔧',
      turns: [
        { speaker: 'clara', speech: 'Discipline and trade are one field, discipline code, matching the Coding Structure three-letter list. The old trade column is not shown on this screen or on exports.' },
        { speaker: 'liam', speech: 'The legend on Hiring and Layoff lists departments, disciplines, and roles. If a timesheet trade looks blank, the master row is missing a discipline code.' },
      ],
    },
    {
      title: 'Entra sync is email only',
      icon: '☁️',
      turns: [
        { speaker: 'liam', speech: 'Sync from Entra matches Employee I D or email and fills the email, then can create a portal user. It does not invent the roster. It does not hide field craft who have no email.' },
        { speaker: 'clara', speech: 'Sync Now refreshes that match. Review stale actives before you deactivate. Never type a fake email to silence the banner. Ball in court on Hiring and Layoff shows who still needs an Entra account.' },
      ],
    },
    {
      title: 'Hiring and layoff path',
      icon: '🔁',
      turns: [
        { speaker: 'clara', speech: 'Tanya adds the four-digit code and name. Jordan creates the Entra email. Daily sync links the portal user. Then that person can open Time Tracker.' },
        { speaker: 'liam', speech: 'Leave or terminated revokes portal access and keeps history. Follow-up notes ride the digest until cleared. The audit log lives on the Accounting screen, not on this thinner card.' },
      ],
    },
    {
      title: 'Crew management',
      icon: '👷',
      turns: [
        { speaker: 'liam', speech: 'Create a crew with yourself as the head, add members, and assign projects. You can change that crew and transfer it to someone else. Accounting and superadmin can change any crew. Every create, edit, transfer, and remove is written to Audit.' },
        { speaker: 'clara', speech: 'Daily stays closed until you head a crew. If a foreman says Daily is empty, open Crews before you reload Op Codes.' },
      ],
    },
    {
      title: 'Payroll period lock',
      icon: '🔒',
      turns: [
        { speaker: 'clara', speech: 'Create Canada and United States periods separately. Locking Canada freezes that week for every Canadian employee, on every job. It is not a lock on one project. The payroll group comes from the division name. A name that ends in U S is the United States. A name that ends in C A is Canada. If the division does not say the country, or the person has no division, the lock does not cover them.' },
        { speaker: 'liam', speech: 'Field edits stop on a locked week. A manager can still correct a row. Unlock only for a true error. Do not lock while people are still entering Friday hours.' },
      ],
    },
    {
      title: 'Maestro payroll export',
      icon: '📤',
      turns: [
        { speaker: 'liam', speech: 'Pick a Canada Sunday to Saturday week or a United States Monday to Sunday week, or a custom range, then the project. Each employee, job, code, and shift can become nine Maestro lines. A zero line is skipped.' },
        { speaker: 'clara', speech: 'Those nine lines are regular, time and a half, double, height, travel hours, meals, room and board, travel dollars, and bus. This file includes drafts and submitted hours. The P D F does not include drafts. Submit or clear drafts before you export.' },
        { speaker: 'liam', speech: 'Removed rows stay out. The presentation job that starts with P R E S O dash T T dash zero zero one stays out. A normal job keeps the labour code you picked. A job that starts with T becomes activity zero two zero zero one. A job number longer than eight characters uses characters ten through fourteen. Expense lines use activity zero one zero one five, except those long job numbers, which keep the derived code.' },
      ],
    },
    {
      title: 'P D F reports',
      icon: '📄',
      turns: [
        { speaker: 'clara', speech: 'Details is every line. Master Payroll is one employee per week with day columns. Op Code Summary rolls contract to employee. Contract Report goes contract, to code, to employee day grid.' },
        { speaker: 'liam', speech: 'Date range, division, and project filters apply. Canada and United States day order follow the week rule. Comments print as day-off notes. Empty report usually means drafts or the wrong week.' },
      ],
    },
    {
      title: 'Analytics before you lock',
      icon: '📉',
      turns: [
        { speaker: 'liam', speech: 'Hours by trade, op code, and shift are a last look before lock and export. A spike on one code is often a mistyped extra or a crew charged to the wrong discipline.' },
        { speaker: 'clara', speech: 'Fix it on the log while the period is open. After lock you are writing a correction story, not a quiet edit.' },
      ],
    },
    {
      title: 'End-to-end payroll checklist',
      icon: '✅',
      turns: [
        { speaker: 'clara', speech: 'Load standard op codes. Confirm Change Log extras landed. Confirm crews. Keep the period open. Let the field submit. The P D F is submitted only. The Maestro file still includes drafts, so clear those badges first. Then lock the payroll group. Then Maestro. Then the P D F pack.' },
        { speaker: 'liam', speech: 'Skip a step and you will see empty dropdowns, missing Daily, or a payroll file that still has drafts the report does not show. Do them in that order every week.' },
      ],
    },
    {
      title: 'Admin troubleshooting',
      icon: '🛠️',
      turns: [
        { speaker: 'liam', speech: 'Empty dropdown: open the job, then the discipline, then load Op Codes or wait for the Change Log extra sync. Export forbidden: you are not in the admin role. Empty P D F: filter or status. Entra mismatch: look at the unmatched actives, not the thirteen hundred total.' },
        { speaker: 'clara', speech: 'Duplicate unique I Ds come from old Excel uploads. Placeholders should be gone before Maestro. If an extra is missing, open the job-discipline pair in Extras. Happy closing.' },
      ],
    },
    {
      title: 'Audit log',
      icon: '🧾',
      turns: [
        { speaker: 'clara', speech: 'The orange Audit button is for Accounting and superadmin only. Daily and Weekly saves write the audit row in the same step as the hours, including room and board, meals, and travel. If that write fails, the hours are not kept.' },
        { speaker: 'liam', speech: 'The audit log lives in the same database as the hours. Filter by type, action, date, or person. Restore a removed row from the same screen. Export a C S V when you close the week.' },
      ],
    },
    {
      title: 'Nothing is erased',
      icon: '♻️',
      turns: [
        { speaker: 'liam', speech: 'Time Tracker does not hard delete. Timesheets, op codes, extras, crews, crew members, and payroll periods are marked removed and hidden. The row stays. Audit can bring it back.' },
        { speaker: 'clara', speech: 'Employees are deactivated, never wiped. A mistaken remove is a restore, not a rebuild from memory.' },
      ],
    },
    {
      title: 'What keeps the tool standing',
      icon: '🛟',
      turns: [
        { speaker: 'clara', speech: 'The hours and the audit log live in Azure Database for PostgreSQL. Azure can restore the whole database to an earlier moment. A separate copy of the Time Tracker tables can be kept outside Azure, so the hours are not only in that one database.' },
        { speaker: 'liam', speech: 'A wrong row is an Audit restore. A lost database is the Azure restore, or that outside copy. Git holds the code, not the hours. That stack is how Time Tracker stays standing.' },
      ],
    },
  ],
};
